/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo } from "react";
import ReactECharts from "echarts-for-react";
import CardLayout from "../Cards/CardLayout";
import { useFetchData } from "../../customHooks/useGetDashboardData";
import Loader from "../feedBacks/loader";
import { COLORS, REFETCH_CONFIGURATION } from "../../utils/constants";
import CardError from "../feedBacks/CardError";
import NoContent from "../feedBacks/NoContent";
import { EChartsOption } from "echarts";
import { useUtils } from "../../customHooks/useUtils";
import { IGeneralChartProps } from "../../utils/interfaces";

const FinancialPerformanceChart = ({
  queryKey,
  route,
  params,
}: IGeneralChartProps) => {
  const { t, FEEDBACK_MESSAGES } = useUtils();

  // Get current year date range for API call
  const currentYear = new Date().getFullYear();
  const start_date = `${currentYear}-01-01`;

  const today = new Date();
  const end_date = today.toISOString().split("T")[0]; // YYYY-MM-DD

  const { data, isLoading, error } = useFetchData<any>(
    queryKey,
    route,
    {
      ...params,
      start_date,
      end_date,
    },
    // @ts-ignore
    {
      ...REFETCH_CONFIGURATION,
    }
  );

  // Transform API data (translate month names) and keep header keys as provided by API.
  const chartData = useMemo(() => {
    if (!data?.dataset) {
      return {
        dataset: [
          ["month", "collection", "billed_energy"], // keep expected header format
        ],
      };
    }

    const transformed = data.dataset.map((row: any, idx: number) => {
      if (idx === 0) return row; // header: ["month","collection","billed_energy"]
      const [monthKey, collection, billed_energy] = row;
      const translatedMonth = t(`month.${monthKey}`); // e.g., "Jan", "Feb"
      return [translatedMonth, collection, billed_energy];
    });

    return { dataset: transformed };
  }, [data, t]);

  // Compute y-axis min, max, interval from both series (collection & billed_energy)
  const yAxisConfig = useMemo(() => {
    const rows = chartData.dataset.slice(1);
    const numeric = rows
      .flatMap((r: any[]) => [r[1], r[2]]) // column 1 = collection, 2 = billed_energy
      .filter((v: any) => typeof v === "number" && !isNaN(v));

    if (!numeric.length) {
      return {
        min: 0,
        max: undefined as number | undefined,
        interval: undefined as number | undefined,
      };
    }

    let minVal = Math.min(...numeric) > 0 ? Math.min(...numeric) : 10;
    let maxVal = Math.max(...numeric);
    // console.log('minValue', minVal)
    if (minVal === maxVal) {
      // expand a bit when flat line
      const pad = minVal === 0 ? 1 : Math.abs(minVal) * 0.1;
      minVal = minVal - pad;
      maxVal = maxVal + pad;
    } else {
      // 5% padding
      const rangePad = (maxVal - minVal) * 0.05;
      minVal = Math.max(0, Math.floor(minVal - rangePad));
      maxVal = Math.ceil(maxVal + rangePad);
    }

    // simple 5-step interval
    const interval = Math.ceil((maxVal - minVal) / 5);

    return { min: minVal, max: maxVal, interval };
  }, [chartData]);

  // console.log('AxisConfig', yAxisConfig)
  const options: EChartsOption = {
    dataset: {
      source: chartData.dataset,
    },
    xAxis: {
      axisLine: {
        show: true,
        lineStyle: { color: "#000", width: 0.8 },
      },
      type: "category",
      name: "Month",
      nameLocation: "middle",
      nameGap: 30,
      min: yAxisConfig.min,
    },
    yAxis: {
      type: "value",
      axisLine: {
        show: true,
        lineStyle: { color: "#000", width: 0.8 },
      },
      name: t("finance.amount"),
      nameLocation: "middle",
      nameGap: 80,
      min: yAxisConfig.min,
      max: yAxisConfig.max,
      interval: yAxisConfig.interval,
      splitLine: {
        lineStyle: { type: "dashed", color: "#eee" },
      },
    },
    series: [
      {
        name: t("chart.collection_label"),
        type: "line",
        encode: {
          x: "month",
          y: "collection", // matches header
        },
        smooth: true,
        symbol: "circle",
        symbolSize: 4,
        itemStyle: { color: COLORS.BLACK },
        areaStyle: {
          color: {
            type: "linear",
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: "#7C878E" },
              { offset: 1, color: "#7C878E00" },
            ],
          },
        },
        lineStyle: { color: "black" },
      },
      {
        name: t("chart.billed_energy_label"),
        type: "line",
        encode: {
          x: "month",
          y: "billed_energy", // <-- FIX: match API's snake_case header
        },
        smooth: true,
        symbol: "circle",
        symbolSize: 4,
        itemStyle: { color: "#F7941D" },
        areaStyle: {
          color: {
            type: "linear",
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: "#fcef7a" },
              { offset: 1, color: "#FFC00000" },
            ],
          },
        },
        lineStyle: { color: "#F7941D" },
      },
    ],
    tooltip: {
      trigger: "axis",
      formatter: (params: any) => {
        let tooltip = `<strong>${params[0].axisValue}</strong><br/>`;
        params.forEach((p: any) => {
          tooltip += `${p.marker} ${p.seriesName}: ${p.data[
            p.seriesIndex + 1
          ]?.toLocaleString()}<br/>`;
        });
        return tooltip;
      },
    },
  };

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className="flex justify-center items-center h-full w-full">
          <Loader />
        </div>
      );
    } else if (data && data.dataset && data.dataset.length > 1) {
      return <ReactECharts option={options} style={{ height: 400 }} />;
    } else if (error) {
      return (
        <CardError message={FEEDBACK_MESSAGES.ERROR} className="!h-[100px]" />
      );
    } else {
      return <NoContent message={FEEDBACK_MESSAGES.NO_DATA} />;
    }
  };

  const legend = [
    { text: t("chart.collection_label"), color: COLORS.BLACK },
    { text: t("chart.billed_energy_label"), color: "#F7941D" },
  ];

  // const showDummyDataBadge = !data || data.dataset?.length <= 1;

  return (
    <CardLayout
      title={t("chart.financial_performance_label")}
      className="min-w-[400px] flex-1"
      besideTitleComponent={
        <div className="flex !w-full justify-between">
          {/* {showDummyDataBadge && (
            <div className="text-sm h-6 flex items-center justify-center rounded-sm p-2 min-w-[50px] bg-primary-yellowMain">
              {t("feedback.dummy_data")}
            </div>
          )} */}
          <div className="flex gap-2 justify-between">
            <div className="flex-1 flex gap-2 justify-end">
              {legend.map((item, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-1 rounded-full bg-primary-blackLighter2 px-3 py-2"
                >
                  <span
                    className="w-4 h-4 rounded-md"
                    style={{ backgroundColor: item.color }}
                  ></span>
                  <span className="text-[11px]">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      }
      besideComponentStyle="!w-full max-w-[70%] !justify-between"
    >
      <div className="flex flex-col h-full w-full relative">{renderBody()}</div>
    </CardLayout>
  );
};

export default FinancialPerformanceChart;
